import { ChannelType, type Call, type Channel, type Guild, type Member, type PresenceActivity, type PresenceStatus, type Relationship, type User, type VoiceState } from '@/types/api'
import { channelPermissions, has, P } from './permissions'
import { isOnline } from './presence'

export interface ActiveFriend { user: User; status: PresenceStatus; streaming: boolean }
export interface FriendActivityGroup {
  id: string
  kind: 'game' | 'voice' | 'call'
  title: string
  subtitle?: string
  steam?: string
  guildId?: string
  channelId?: string
  friends: ActiveFriend[]
}
interface ActivityContext {
  selfId: string | undefined
  relationships: Record<string, Relationship>
  presences: Record<string, PresenceStatus>
  activities: Record<string, PresenceActivity[]>
  guilds: Record<string, Guild>
  channels: Record<string, Record<string, Channel>>
  members: Record<string, Record<string, Member>>
  voiceStates: Record<string, Record<string, VoiceState>>
  privateChannels: Record<string, Channel>
  calls: Record<string, Call>
  callStates: Record<string, Record<string, VoiceState>>
}

/** Только друзья в сети; комнаты — из доступных серверов и собственных бесед. */
export function friendActivity(ctx: ActivityContext): FriendActivityGroup[] {
  if (!ctx.selfId) return []
  const friends = new Map(Object.values(ctx.relationships).filter(r => r.type === 1 && r.id !== ctx.selfId && !r.user.bot && isOnline(ctx.presences[r.id])).map(r => [r.id, r.user]))
  const groups: FriendActivityGroup[] = []
  const activeIn = (states: Record<string, VoiceState>, channelId: string): ActiveFriend[] => Object.values(states).flatMap(vs => {
    const user = friends.get(vs.user_id)
    return user && vs.channel_id === channelId ? [{ user, status: ctx.presences[user.id]!, streaming: vs.self_stream }] : []
  }).sort((a, b) => Number(b.streaming) - Number(a.streaming) || (a.user.global_name ?? a.user.username).localeCompare(b.user.global_name ?? b.user.username, 'ru'))
  for (const guild of Object.values(ctx.guilds)) {
    const viewer = ctx.members[guild.id]?.[ctx.selfId] ?? { user: { id: ctx.selfId }, roles: [] }
    for (const ch of Object.values(ctx.channels[guild.id] ?? {})) {
      if (ch.type !== ChannelType.GuildVoice || !has(channelPermissions(guild, viewer, ch), P.VIEW_CHANNEL)) continue
      const active = activeIn(ctx.voiceStates[guild.id] ?? {}, ch.id)
      if (active.length) groups.push({ id: `voice:${ch.id}`, kind: 'voice', title: ch.name ?? 'Голосовой канал', subtitle: guild.name, guildId: guild.id, channelId: ch.id, friends: active })
    }
  }
  for (const call of Object.values(ctx.calls)) {
    const ch = ctx.privateChannels[call.channel_id]
    if (!ch || (ch.type !== ChannelType.DM && ch.type !== ChannelType.GroupDM)) continue
    const active = activeIn(ctx.callStates[ch.id] ?? {}, ch.id)
    if (active.length) groups.push({ id: `call:${ch.id}`, kind: 'call', title: ch.type === ChannelType.GroupDM ? ch.name?.trim() || 'Групповой звонок' : 'Личный звонок', channelId: ch.id, friends: active })
  }
  const games = new Map<string, FriendActivityGroup>()
  for (const [id, user] of friends) {
    const game = ctx.activities[id]?.find(a => a.type === 0 && a.name.trim())
    if (!game) continue
    const name = game.name.trim(), key = name.toLocaleLowerCase()
    const group = games.get(key) ?? { id: `game:${key}`, kind: 'game' as const, title: name, friends: [], ...(game.steam_app_id ? { steam: game.steam_app_id } : {}) }
    group.friends.push({ user, status: ctx.presences[id]!, streaming: false })
    games.set(key, group)
  }
  groups.push(...games.values())
  return groups.sort((a, b) => Number(b.friends.some(f => f.streaming)) - Number(a.friends.some(f => f.streaming)) || Number(a.kind === 'game') - Number(b.kind === 'game') || a.title.localeCompare(b.title, 'ru'))
}
